import React, { useState } from 'react';
import { Calendar, User, Info, Link as LinkIcon, Download, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Modal } from '../../../components/common/Modal';
import { RoastProfileSection } from '../../roasts/component/RoastProfile';
import { RoastResultSection } from '../../roasts/component/RoastResultSection';
import type { RoastProfileRow, RoastStageState } from '../../roasts/component/types';
import { artisanProfileService, type ArtisanProfileDTO } from '../../../services/artisanProfileService';
import type { WorkOrder } from '../../../types/workOrder';
import { isEligibleArtisanSyncTarget } from '../eligibleWorkOrders';
import { useLanguage } from '../../../features/language/useLanguage';
import { MetadataCard } from './MetadataCard';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    profile: ArtisanProfileDTO | undefined;
    isLoading: boolean;
    canWrite: boolean;
    canDelete: boolean;
    workOrders: WorkOrder[] | undefined;
    isLoadingWorkOrders: boolean;
    isSyncModalOpen: boolean;
    onOpenSyncModal: () => void;
    onCloseSyncModal: () => void;
    onSync: (workOrderId: string) => void;
    isSyncing: boolean;
    syncError: string | null;
    onDelete: () => void;
}

export const ArtisanProfileDetailModal: React.FC<Props> = ({
    isOpen,
    onClose,
    profile,
    isLoading,
    canWrite,
    canDelete,
    workOrders,
    isLoadingWorkOrders,
    isSyncModalOpen,
    onOpenSyncModal,
    onCloseSyncModal,
    onSync,
    isSyncing,
    syncError,
    onDelete,
}) => {
    const { t } = useLanguage();
    const [selectedWorkOrderId, setSelectedWorkOrderId] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);

    if (!isOpen) return null;

    if (isLoading || !profile) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={t('artisanSync.detail.loadingTitle')}
                maxWidth="7xl"
                className="max-h-[90vh] overflow-y-auto p-4 sm:p-6"
            >
                <div className="p-12 text-center text-gray-500 animate-pulse">{t('artisanSync.detail.loadingBody')}</div>
            </Modal>
        );
    }

    // The endpoint needs the Authorization header, so a plain <a href> would 401. Fetching
    // as a blob and clicking a synthetic link is what carries the token through.
    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const blob = await artisanProfileService.downloadFile(profile.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = profile.raw_file_name || 'roast.alog';
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setIsDownloading(false);
        }
    };

    // Reconstruct stages from DB format for the UI
    const readStage = (key: RoastStageState['key'], label: string): RoastStageState => {
        const stage = profile.recipe?.stage_summary?.[key];
        const tempValue = stage?.temp;
        let temp: number | '' = '';
        if (typeof tempValue === 'number') {
            temp = tempValue;
        } else if (typeof tempValue === 'string') {
            const parsed = Number(tempValue);
            temp = Number.isFinite(parsed) ? parsed : '';
        }
        return { key, label, time: stage?.time || '00:00', temp };
    };

    const reconstructedStages: RoastStageState[] = [
        readStage('start', t('roast.profile.stages.start')),
        readStage('turning_point', t('roast.profile.stages.turningPoint')),
        readStage('yellow', t('roast.profile.stages.yellow')),
        readStage('first_crack', t('roast.profile.stages.firstCrack')),
        readStage('end', t('roast.profile.stages.end')),
    ];

    const initialProfile: RoastProfileRow[] = Array.isArray(profile.recipe?.profile)
        ? (profile.recipe?.profile as RoastProfileRow[])
        : [];

    const eligibleWorkOrders = (workOrders || []).filter(isEligibleArtisanSyncTarget);

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                setSelectedWorkOrderId('');
                onClose();
            }}
            title={profile.title || t('artisanSync.values.untitledRoast')}
            maxWidth="7xl"
            className="max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        >
            <div className="space-y-6">
                {/* Stacks below the meta line on phones: the badge plus up to three actions
                    never fit one row there, and forcing them to would scroll the whole modal. */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <p className="text-sm text-gray-500 sm:mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
                        <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 flex-shrink-0" /> {profile.roast_date ? format(new Date(profile.roast_date), 'MMM d, yyyy') : '-'} {t('artisanSync.detail.at')} {profile.roast_time || '-'}</span>
                        <span className="flex items-center gap-1.5 min-w-0"><User className="h-4 w-4 flex-shrink-0" /> <span className="truncate">{profile.roaster || t('artisanSync.values.unknownOperator')}</span></span>
                    </p>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:justify-end sm:flex-shrink-0">
                        {profile.is_synced ? (
                            <span className="inline-flex items-center gap-1.5 max-w-full px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                                <LinkIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                {t('artisanSync.detail.syncedToWo', { workOrderNumber: profile.work_order_number || '-' })}
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 max-w-full px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                <Info className="h-3.5 w-3.5 flex-shrink-0" />
                                {t('artisanSync.detail.unsyncedProfile')}
                            </span>
                        )}

                        {/* An absent file is stated rather than left as a missing button, so it
                            reads as explained instead of broken. */}
                        {profile.has_raw_file ? (
                            <button
                                onClick={handleDownload}
                                disabled={isDownloading}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                data-test-id="artisan-download-file"
                            >
                                <Download className="h-4 w-4" />
                                {isDownloading ? t('artisanSync.file.downloading') : t('artisanSync.file.download')}
                            </button>
                        ) : (
                            <span className="text-xs text-gray-400" title={t('artisanSync.file.notStored')}>
                                {t('artisanSync.file.notStored')}
                            </span>
                        )}

                        {canWrite && !profile.is_synced && (
                            <button
                                onClick={() => {
                                    setSelectedWorkOrderId('');
                                    onOpenSyncModal();
                                }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                data-test-id="artisan-sync-to-wo-button"
                            >
                                <LinkIcon className="h-4 w-4" />
                                {t('artisanSync.detail.syncToWo')}
                            </button>
                        )}

                        {canDelete && !profile.is_synced && (
                            <button
                                onClick={onDelete}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
                                data-test-id="artisan-delete-profile"
                            >
                                <Trash2 className="h-4 w-4" />
                                {t('artisanSync.delete.action')}
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                    <MetadataCard label={t('artisanSync.meta.machine')} value={profile.machine || '-'} />
                    <MetadataCard label={t('artisanSync.meta.drumSpeed')} value={profile.drum_speed ? `${profile.drum_speed} RPM` : '-'} />
                </div>

                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 shadow-sm min-w-0">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{t('artisanSync.detail.roastChart')}</h3>
                    <RoastProfileSection seconds={0} stage="idle" mode="recipe" initialProfile={initialProfile} readOnly stages={reconstructedStages} />
                </div>

                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 shadow-sm min-w-0">
                    <RoastResultSection
                        stages={reconstructedStages}
                        onStageChange={() => { }}
                        weightInput={profile.recipe?.input_weight || undefined}
                        calculatedWeight={profile.recipe?.input_weight || 0}
                        onWeightInputChange={() => { }}
                        weightOutput={profile.recipe?.output_weight || undefined}
                        onWeightOutputChange={() => { }}
                        agtronWhole={
                            typeof profile.recipe?.agtron?.wholeBean === 'number'
                                ? String(profile.recipe.agtron.wholeBean)
                                : ''
                        }
                        agtronGround={
                            typeof profile.recipe?.agtron?.groundBean === 'number'
                                ? String(profile.recipe.agtron.groundBean)
                                : ''
                        }
                        onAgtronChange={() => { }}
                        roastType={profile.recipe?.roast_type || ''}
                        onRoastTypeChange={() => { }}
                        readOnly
                    />
                </div>

                <Modal
                    isOpen={isSyncModalOpen}
                    onClose={() => {
                        setSelectedWorkOrderId('');
                        onCloseSyncModal();
                    }}
                    title={t('artisanSync.syncModal.title')}
                    maxWidth="2xl"
                    zIndexClass="z-50"
                >
                    <p className="text-sm text-gray-500">{t('artisanSync.syncModal.description')}</p>
                    <div className="mt-4 space-y-4">
                        {syncError && (
                            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                                {syncError}
                            </div>
                        )}

                        {isLoadingWorkOrders ? (
                            <div className="text-sm text-gray-500">{t('artisanSync.syncModal.loadingWo')}</div>
                        ) : eligibleWorkOrders.length === 0 ? (
                            <div className="text-sm text-gray-500">{t('artisanSync.syncModal.emptyWo')}</div>
                        ) : (
                            <div className="space-y-2 max-h-72 overflow-y-auto border rounded-md">
                                {eligibleWorkOrders.map(wo => (
                                    <label
                                        key={wo.id}
                                        className="flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                                    >
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="radio"
                                                name="workOrderId"
                                                value={wo.id}
                                                checked={selectedWorkOrderId === wo.id}
                                                onChange={() => setSelectedWorkOrderId(wo.id)}
                                                className="h-4 w-4 text-primary-600 border-gray-300"
                                                data-test-id="artisan-sync-wo-radio"
                                            />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{wo.workOrderNumber || wo.recipeName || t('artisanSync.values.workOrder')}</div>
                                                <div className="text-xs text-gray-500">
                                                    {wo.status} • {wo.targetDate ? format(new Date(wo.targetDate), 'MMM d, yyyy') : '-'}
                                                </div>
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-5 flex items-center justify-end gap-3">
                        <button
                            onClick={() => {
                                setSelectedWorkOrderId('');
                                onCloseSyncModal();
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            data-test-id="artisan-sync-cancel"
                        >
                            {t('auth.common.cancel')}
                        </button>
                        <button
                            onClick={() => {
                                if (!selectedWorkOrderId) return;
                                onSync(selectedWorkOrderId);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                            disabled={!selectedWorkOrderId || isSyncing}
                            data-test-id="artisan-sync-confirm"
                        >
                            {isSyncing ? t('artisanSync.syncModal.syncing') : t('artisanSync.syncModal.syncNow')}
                        </button>
                    </div>
                </Modal>
            </div>
        </Modal>
    );
};
